import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { selectCompanyId, selectUserId } from '@/redux/features/authSlice';
import { getStore } from '@/redux/store';
import SentryService from '@/utils/sentry-service.util';

/**
 * Amplitude Analytics Service
 *
 * Handles direct integration with Amplitude analytics.
 * This service works alongside Segment tracking and backend API.
 */
export class AmplitudeService {
  private static initialized = false;
  private static apiKey: string | undefined;

  /**
   * Logs a warning message in development mode
   * @param message Warning message to log
   */
  private static logWarning(message: string): void {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(message);
    }
  }

  /**
   * Logs a debug message in development mode
   * @param message Debug message to log
   */
  private static logDebug(message: string): void {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug(message);
    }
  }

  /**
   * Checks if the service is initialized and logs a message if not
   * @param action Action name for the log message
   * @param context Optional additional context for the log message
   * @returns true if initialized, false otherwise
   */
  private static ensureInitialized(action: string, context?: string): boolean {
    if (!this.initialized) {
      const message = context
        ? `Amplitude not initialized, skipping ${ action }: ${ context }`
        : `Amplitude not initialized, skipping ${ action }`;
      this.logDebug(message);
      return false;
    }
    return true;
  }

  /**
   * Initializes Amplitude with the provided API key
   * @param apiKey Amplitude API key
   * @param options Optional Amplitude configuration options
   */
  static init(apiKey: string, options?: amplitude.Types.BrowserOptions): void {
    if (this.initialized) {
      this.logWarning('Amplitude already initialized');
      return;
    }

    if (!apiKey) {
      this.logWarning('Amplitude API key not provided');
      return;
    }

    // Create and Install Session Replay Plugin
    const sessionReplayTracking = sessionReplayPlugin({
      forceSessionTracking: true,
      sampleRate: 0.25,
    });
    amplitude.add(sessionReplayTracking);

    this.apiKey = apiKey;

    try {
      amplitude.init(apiKey, undefined, {
        autocapture: {
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: false,
          elementInteractions: true,
        },
        ...options,
      });

      this.initialized = true;
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to initialize Amplitude',
          apiKeyPresent: !!apiKey,
        },
      });
    }
  }

  /**
   * Tracks an event in Amplitude
   * @param event Event name
   * @param properties Event properties
   */
  static track(event: string, properties?: Record<string, unknown>): void {
    if (!this.ensureInitialized('track', event)) {
      return;
    }

    try {
      const store = getStore();
      const state = store.getState();
      const userId = selectUserId(state as Parameters<typeof selectUserId>[0]);
      const companyId = selectCompanyId(state as Parameters<typeof selectCompanyId>[0]);

      // Combine userId and companyId in format: userId|companyId
      // Use only userId if companyId is missing/empty
      // Use undefined if userId is null/undefined
      let amplitudeUserId: string | undefined;
      if (userId) {
        amplitudeUserId = companyId ? `${ userId }|${ companyId }` : userId;
      }

      amplitude.track(event, properties, {
        user_id: amplitudeUserId,
      });
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to track Amplitude event',
          event,
          properties,
        },
      });
    }
  }

  /**
   * Sets a user property on an Identify instance
   * @param identify Identify instance to set properties on
   * @param key Property key
   * @param value Property value
   */
  private static setUserProperty(
    identify: amplitude.Identify,
    key: string,
    value: unknown,
  ): void {
    if (value === null || value === undefined) {
      return;
    }

    // Amplitude accepts: string, number, boolean, or array of string/number
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      identify.set(key, value);
      return;
    }

    if (Array.isArray(value)) {
      // Arrays must contain only strings or numbers (not booleans)
      const validArray: (string | number)[] = value.filter(
        (item): item is string | number =>
          typeof item === 'string' || typeof item === 'number',
      );
      if (validArray.length > 0) {
        identify.set(key, validArray);
      }
    }
  }

  /**
   * Identifies a user with Amplitude
   * @param userId User ID
   * @param userProperties User properties/traits
   */
  static identify(userId: string, userProperties?: Record<string, unknown>): void {
    if (!this.ensureInitialized('identify')) {
      return;
    }

    try {
      const identify = new amplitude.Identify();

      if (userProperties) {
        for (const [key, value] of Object.entries(userProperties)) {
          this.setUserProperty(identify, key, value);
        }
      }

      amplitude.identify(identify, {
        user_id: userId,
      });
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to identify user in Amplitude',
          userId,
        },
      });
    }
  }

  /**
   * Sets the user ID for Amplitude
   * @param userId User ID
   */
  static setUserId(userId: string): void {
    if (!this.ensureInitialized('setUserId')) {
      return;
    }

    try {
      amplitude.setUserId(userId);
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to set user ID in Amplitude',
          userId,
        },
      });
    }
  }

  /**
   * Sets the user's group membership in Amplitude
   * @param groupType Group type (e.g., 'company')
   * @param groupId Group ID
   */
  static setGroup(groupType: string, groupId: string): void {
    if (!this.ensureInitialized('setGroup')) {
      return;
    }

    try {
      amplitude.setGroup(groupType, groupId);
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to set group in Amplitude',
          groupType,
          groupId,
        },
      });
    }
  }

  /**
   * Identifies a group with Amplitude
   * @param groupType Group type (e.g., 'company')
   * @param groupId Group ID
   * @param groupProperties Group properties
   */
  static groupIdentify(
    groupType: string,
    groupId: string,
    groupProperties: {
      name?: string;
      plan?: string;
      createdate?: string;
      status?: string;
      trialstartdate?: string;
      trialenddate?: string;
      painpoint?: string;
      onboardingexpectedteamsize?: string;
    },
  ): void {
    if (!this.ensureInitialized('groupIdentify')) {
      return;
    }

    try {
      const identify = new amplitude.Identify();

      if (groupProperties.name) {
        identify.set('name', groupProperties.name);
      }
      if (groupProperties.plan) {
        identify.set('plan', groupProperties.plan);
      }
      if (groupProperties.createdate) {
        identify.set('createdate', groupProperties.createdate);
      }
      if (groupProperties.status) {
        identify.set('status', groupProperties.status);
      }
      if (groupProperties.trialstartdate) {
        identify.set('trialstartdate', groupProperties.trialstartdate);
      }
      if (groupProperties.trialenddate) {
        identify.set('trialenddate', groupProperties.trialenddate);
      }
      if (groupProperties.painpoint) {
        identify.set('painpoint', groupProperties.painpoint);
      }
      if (groupProperties.onboardingexpectedteamsize) {
        identify.set('onboardingexpectedteamsize', groupProperties.onboardingexpectedteamsize);
      }

      amplitude.groupIdentify(groupType, groupId, identify);
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to identify group in Amplitude',
          groupType,
          groupId,
          groupProperties,
        },
      });
    }
  }

  /**
   * Sets the user ID for Amplitude Session Replay
   * @param userId User ID in format 'tduserid|tdcompanyid'
   */
  static setSessionReplayUserId(userId: string): void {
    if (!this.ensureInitialized('setSessionReplayUserId')) {
      return;
    }

    try {
      // @ts-expect-error: sessionReplay may not be in type definitions but exists at runtime
      if (amplitude.sessionReplay && typeof amplitude.sessionReplay.setUserId === 'function') {
        // @ts-expect-error: sessionReplay may not be in type definitions but exists at runtime
        amplitude.sessionReplay.setUserId(userId);
      }
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to set session replay user ID in Amplitude',
          userId,
        },
      });
    }
  }

  /**
   * Sets the group for Amplitude Session Replay
   * @param groupType Group type (e.g., 'company')
   * @param groupId Group ID
   */
  static setSessionReplayGroup(groupType: string, groupId: string): void {
    if (!this.ensureInitialized('setSessionReplayGroup')) {
      return;
    }

    try {
      // @ts-expect-error: sessionReplay may not be in type definitions but exists at runtime
      if (amplitude.sessionReplay && typeof amplitude.sessionReplay.setGroup === 'function') {
        // @ts-expect-error: sessionReplay may not be in type definitions but exists at runtime
        amplitude.sessionReplay.setGroup(groupType, groupId);
      }
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to set session replay group in Amplitude',
          groupType,
          groupId,
        },
      });
    }
  }

  /**
   * Resets user identity (e.g., on logout)
   */
  static reset(): void {
    if (!this.initialized) return;

    try {
      amplitude.reset();
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to reset Amplitude',
        },
      });
    }
  }

  /**
   * Flushes pending events to Amplitude
   */
  static flush(): void {
    if (!this.initialized) return;

    try {
      amplitude.flush();
    } catch (error) {
      SentryService.captureExceptionWithRetryAfter(error, {
        extra: {
          errorContext: 'Failed to flush Amplitude events',
        },
      });
    }
  }

  /**
   * Checks if Amplitude is initialized
   */
  static isInitialized(): boolean {
    return this.initialized;
  }
}

